import { identity } from 'lodash'

import selectors from '../selectors'
import {
  DELETE_SELECTED_RELATIONSHIP,
  NEW_RELATIONSHIP_TYPE,
  SET_CURRENT_FAMILY_TREE_BACKGROUND,
  SET_SELECTED_RELATIONSHIP_TYPE,
  SET_SELECTED_RELATIONSHIP_NAME,
  SET_RELATIONSHIP_TYPE_COLOUR,
  SAVE_FAMILY_TREE,
  NEW_FAMILY_TREE,
  DELETE_FAMILY_TREE,
  EDIT_FAMILY_TREE_NAME,
  LOAD_FAMILY_TREES,
  LOAD_FAMILY_TREE,
  BATCH_LOAD_FAMILY_TREE,
  REMOVE_FAMILY_TREE,
  SET_SELECTED_RELATIONSHIP_LINE_STYLE,
  REMOVE_FAMILY_TREE_BACKGROUND_IMAGE,
  SET_FAMILY_TREE_BACKGROUND_COLOR,
} from '../constants/ActionTypes'

/**
 * @typedef {import('../types/plottr').LineStyle} LineStyle
 * @typedef {import('../types/actions').Dispatch} Dispatch
 * @typedef {import('../types/actions').GetState} GetState
 * @typedef {import('../index').StorageURL} StorageURL
 * @typedef {import('../index').ImageDataURI} ImageDataURI
 * @typedef {import('../types/plottr').Translation} Translation
 * @typedef {import('../types/plottr').FamilyTree} FamilyTree
 * @typedef {import('../types/plottr').PlottrFileFamilyTrees} FamilyTrees
 */

const {
  selectedRelationshipTypeSelector,
  currentFamilyTreeRelationshipConfigurationSelector,
  currentFamilyTreeIdSelector,
} = selectors(identity)

/**
 * @typedef {ReturnType<typeof saveFamilyTree> | ReturnType<typeof _setSelectedRelationshipName> | ReturnType<typeof _setSelectedRelationshipType> | ReturnType<typeof _setSelectedRelationshipLineStyle> | ReturnType<typeof _deleteSelectedRelationship> | ReturnType<typeof _newRelationshipType> | ReturnType<typeof deleteFamilyTreeById> | ReturnType<typeof newFamilyTree> | ReturnType<typeof editFamilyTreeName> | ReturnType<typeof removeFamilyTreeBackgroundImage> | ReturnType<typeof setFamilyTreeBackgroundColor> | ReturnType<typeof load> | ReturnType<typeof loadSingle> | ReturnType<typeof batchLoad> | ReturnType<typeof removeSingle> | ReturnType<typeof _setCurrentFamilyTreeBackground> | ReturnType<typeof _chooseRelationshipTypeColour>} FamilyTreeActions
 */

/**
 * @param {FamilyTree} familyTree
 * @return {{ type: 'SAVE_FAMILY_TREE', familyTree: FamilyTree }}
 */
export const saveFamilyTree = (familyTree) => {
  return {
    type: SAVE_FAMILY_TREE,
    familyTree,
  }
}

/**
 * @param {number} currentFamilyTreeId
 * @param {String | Null} backgroundImageId
 * @param {Translation} translation
 * @return {{ type: SET_CURRENT_FAMILY_TREE_BACKGROUND, familyTreeId: number, backgroundImageId: String | Null, translation: Translation }}
 */
export const _setCurrentFamilyTreeBackground = (
  backgroundImageId,
  translation,
  currentFamilyTreeId
) => {
  return {
    type: SET_CURRENT_FAMILY_TREE_BACKGROUND,
    familyTreeId: currentFamilyTreeId,
    backgroundImageId,
    translation,
  }
}

/**
 * @param {String | Null} backgroundImageId
 * @param {Translation} translation
 * @return {function(Dispatch, GetState): Void}
 */
export const setCurrentFamilyTreeBackground =
  (backgroundImageId, translation) => (dispatch, getState) => {
    const currentFamilyTreeId = currentFamilyTreeIdSelector(getState())
    if (typeof currentFamilyTreeId === 'number') {
      dispatch(_setCurrentFamilyTreeBackground(backgroundImageId, translation, currentFamilyTreeId))
    }
  }

/**
 * @param {String} colour
 * @param {number} familyTreeId
 * @param {String} selectedRelationship
 * @return {{ type: SET_RELATIONSHIP_TYPE_COLOUR, familyTreeId: number, colour: String, selectedRelationship: String }}
 */
export const _chooseRelationshipTypeColour = (colour, familyTreeId, selectedRelationship) => {
  return {
    type: SET_RELATIONSHIP_TYPE_COLOUR,
    familyTreeId,
    colour,
    selectedRelationship,
  }
}

/**
 * @param {String} colour
 * @return {function(Dispatch, GetState): Void}
 */
export const chooseRelationshipTypeColour = (colour) => (dispatch, getState) => {
  const currentFamilyTreeId = currentFamilyTreeIdSelector(getState())
  const selectedRelationship = selectedRelationshipTypeSelector(getState())
  if (typeof currentFamilyTreeId === 'number' && selectedRelationship) {
    dispatch(_chooseRelationshipTypeColour(colour, currentFamilyTreeId, selectedRelationship))
  }
}

/**
 * @param {String} name
 * @param {number} familyTreeId
 * @param {String} selectedRelationship
 * @return {{ type: 'SET_SELECTED_RELATIONSHIP_NAME', familyTreeId: Number, name: String, selectedRelationship: String }}
 */
export const _setSelectedRelationshipName = (name, familyTreeId, selectedRelationship) => {
  return {
    type: SET_SELECTED_RELATIONSHIP_NAME,
    familyTreeId,
    name,
    selectedRelationship,
  }
}

/**
 * @param {String} name
 * @return {function(Dispatch, GetState): Void}
 */
export const setSelectedRelationshipName = (name) => (dispatch, getState) => {
  const currentFamilyTreeId = currentFamilyTreeIdSelector(getState())
  const selectedRelationship = selectedRelationshipTypeSelector(getState())
  if (typeof currentFamilyTreeId === 'number' && selectedRelationship) {
    dispatch(_setSelectedRelationshipName(name, currentFamilyTreeId, selectedRelationship))
  }
}

/**
 * @param {String} type
 * @param {number} currentFamilyTreeId
 * @param {String} selectedRelationship
 * @return {{ type: 'SET_SELECTED_RELATIONSHIP_TYPE', familyTreeId: number, relationshipType: String, selectedRelationship: String }}
 */
export const _setSelectedRelationshipType = (type, currentFamilyTreeId, selectedRelationship) => {
  return {
    type: SET_SELECTED_RELATIONSHIP_TYPE,
    familyTreeId: currentFamilyTreeId,
    relationshipType: type,
    selectedRelationship,
  }
}

/**
 * @param {String} type
 * @return {function(Dispatch, GetState): Void}
 */
export const setSelectedRelationshipType = (type) => (dispatch, getState) => {
  const currentFamilyTreeId = currentFamilyTreeIdSelector(getState())
  const selectedRelationship = selectedRelationshipTypeSelector(getState())
  if (typeof currentFamilyTreeId === 'number' && selectedRelationship) {
    dispatch(_setSelectedRelationshipType(type, currentFamilyTreeId, selectedRelationship))
  }
}

/**
 * @param {LineStyle} lineStyle
 * @param {number} familyTreeId
 * @param {String} selectedRelationship
 * @return {{ type: 'SET_SELECTED_RELATIONSHIP_LINE_STYLE', familyTreeId: number, lineStyle: LineStyle, selectedRelationship: String }}
 */
export const _setSelectedRelationshipLineStyle = (
  lineStyle,
  familyTreeId,
  selectedRelationship
) => {
  return {
    type: SET_SELECTED_RELATIONSHIP_LINE_STYLE,
    familyTreeId,
    lineStyle,
    selectedRelationship,
  }
}

/**
 * @param {LineStyle} lineStyle
 * @return {function(Dispatch, GetState): Void}
 */
export const setSelectedRelationshipLineStyle = (lineStyle) => (dispatch, getState) => {
  const currentFamilyTreeId = currentFamilyTreeIdSelector(getState())
  const selectedRelationship = selectedRelationshipTypeSelector(getState())
  if (typeof currentFamilyTreeId === 'number' && selectedRelationship) {
    dispatch(
      _setSelectedRelationshipLineStyle(lineStyle, currentFamilyTreeId, selectedRelationship)
    )
  }
}

/**
 * @param {number} familyTreeId
 * @param {String} selectedRelationship
 * @param {String} newSelectedRelationshipName
 * @return {{ type: 'DELETE_SELECTED_RELATIONSHIP', familyTreeId: number, selectedRelationship: String, newSelectedRelationshipName: String }}
 */
export const _deleteSelectedRelationship = (
  familyTreeId,
  selectedRelationship,
  newSelectedRelationshipName
) => {
  return {
    type: DELETE_SELECTED_RELATIONSHIP,
    familyTreeId,
    selectedRelationship,
    newSelectedRelationshipName,
  }
}

/**
 * @return {function(Dispatch, GetState): Void}
 */
export const deleteSelectedRelationship = () => (dispatch, getState) => {
  const currentFamilyTreeId = currentFamilyTreeIdSelector(getState())
  const selectedRelationship = selectedRelationshipTypeSelector(getState())
  const currentRelationshipConfiguration =
    currentFamilyTreeRelationshipConfigurationSelector(getState())
  const newSelectedRelationshipName =
    Object.values(currentRelationshipConfiguration ?? {}).find((relationshipType) => {
      return relationshipType.name !== selectedRelationship
    })?.name || null
  if (
    typeof currentFamilyTreeId === 'number' &&
    selectedRelationship &&
    newSelectedRelationshipName
  ) {
    dispatch(
      _deleteSelectedRelationship(
        currentFamilyTreeId,
        selectedRelationship,
        newSelectedRelationshipName
      )
    )
  }
}

/**
 * @param {Number} familyTreeId
 * @param {String} relationshipName
 * @return {{ type: 'NEW_RELATIONSHIP_TYPE', familyTreeId: number, relationshipName: String }}
 */
export const _newRelationshipType = (familyTreeId, relationshipName) => {
  return {
    type: NEW_RELATIONSHIP_TYPE,
    familyTreeId,
    relationshipName,
  }
}

/**
 * @return {function(Dispatch, GetState): Void}
 */
export const newRelationshipType = () => (dispatch, getState) => {
  const currentFamilyTreeId = currentFamilyTreeIdSelector(getState())
  const currentRelationshipConfiguration =
    currentFamilyTreeRelationshipConfigurationSelector(getState())
  let i = null
  if (currentRelationshipConfiguration?.[`New Relationship`]) {
    i = 1
    while (currentRelationshipConfiguration?.[`New Relationship ${i}`]) {
      i++
      // Prevent infinite loops by limiting the number of New Relationship N's to 100
      if (i > 100) {
        return
      }
    }
  }
  const relationshipName = i === null ? 'New Relationship' : `New Relationship ${i}`
  if (typeof currentFamilyTreeId === 'number') {
    dispatch(_newRelationshipType(currentFamilyTreeId, relationshipName))
  }
}

/**
 * @param {number} id
 * @return {{ type: 'DELETE_FAMILY_TREE', id: number }}
 */
export const deleteFamilyTreeById = (id) => {
  return {
    type: DELETE_FAMILY_TREE,
    id,
  }
}

/**
 * @return {{ type: 'NEW_FAMILY_TREE' }}
 */
export const newFamilyTree = () => {
  return {
    type: NEW_FAMILY_TREE,
  }
}

/**
 * @param {number} id
 * @param {String} name
 * @return {{ type: 'EDIT_FAMILY_TREE_NAME', id: number, name: String }}
 */
export const editFamilyTreeName = (id, name) => {
  return {
    type: EDIT_FAMILY_TREE_NAME,
    id,
    name,
  }
}

/**
 * Remove the background from the family tree with the provided id.
 *
 * @param {number} id
 * @return {{ type: 'REMOVE_FAMILY_TREE_BACKGROUND_IMAGE', id: number }}
 */
export const removeFamilyTreeBackgroundImage = (id) => {
  return {
    id,
    type: REMOVE_FAMILY_TREE_BACKGROUND_IMAGE,
  }
}

/**
 * Set the background colour of the family tree with the provided id.
 *
 * @param {String | Null} color
 * @param {number} familyTreeId
 * @return {{ type: 'SET_FAMILY_TREE_BACKGROUND_COLOR', color: String | Null, familyTreeId: number }}
 */
export const setFamilyTreeBackgroundColor = (color, familyTreeId) => {
  return {
    type: SET_FAMILY_TREE_BACKGROUND_COLOR,
    color,
    familyTreeId,
  }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {FamilyTrees} familyTrees
 * @return {{ type: 'LOAD_FAMILY_TREES', patching: boolean, familyTrees: FamilyTrees }}
 */
export function load(patching, familyTrees) {
  return { type: LOAD_FAMILY_TREES, patching, familyTrees }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {FamilyTree} familyTree
 * @return {{ type: 'LOAD_FAMILY_TREE', patching: boolean, familyTree: FamilyTree }}
 */
export function loadSingle(patching, familyTree) {
  return { type: LOAD_FAMILY_TREE, patching, familyTree }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {FamilyTrees} familyTrees
 * @return {{ type: 'BATCH_LOAD_FAMILY_TREE', patching: boolean, familyTrees: FamilyTrees }}
 */
export function batchLoad(patching, familyTrees) {
  return { type: BATCH_LOAD_FAMILY_TREE, patching, familyTrees }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {FamilyTree} familyTree
 * @return {{ type: 'REMOVE_FAMILY_TREE', patching: boolean, familyTree: FamilyTree }}
 */
export function removeSingle(patching, familyTree) {
  return { type: REMOVE_FAMILY_TREE, patching, familyTree }
}
